import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getTrendingData,
  TIME_FILTERS,
  type TimeFilter,
  type TrendingRadio,
  type GrowingRadio,
  type PopularLive,
  type FeaturedEvent,
} from "@/mocks/trending";
import Swal from "sweetalert2";

export default function AppTrendingPage() {
  const navigate = useNavigate();
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("today");

  const data = useMemo(() => getTrendingData(timeFilter), [timeFilter]);

  const handlePlay = (name: string) => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: `Reproduciendo ${name}`,
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
  };

  const handleFavorite = (name: string) => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: `${name} agregada a favoritas`,
      showConfirmButton: false,
      timer: 1800,
      timerProgressBar: true,
    });
  };

  const handleShare = (name: string) => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: `Enlace de ${name} copiado`,
      showConfirmButton: false,
      timer: 1500,
      timerProgressBar: true,
    });
  };

  const handleEventReminder = (title: string) => {
    Swal.fire({
      title: "¡Recordatorio creado!",
      html: `Te avisaremos cuando <strong>${title}</strong> esté por comenzar.`,
      icon: "success",
      confirmButtonText: "¡Entendido!",
      confirmButtonColor: "#0f172a",
      customClass: { popup: "rounded-2xl" },
    });
  };

  const handleEventDetail = (event: FeaturedEvent) => {
    Swal.fire({
      title: event.title,
      html: `
        <div class="text-left space-y-3">
          <div class="flex items-center gap-2">
            <img src="${event.stationLogo}" alt="${event.stationName}" class="w-10 h-10 rounded-lg object-cover" />
            <span class="font-semibold text-foreground-950">${event.stationName}</span>
          </div>
          <div class="flex items-center gap-2 text-sm text-foreground-500">
            <i class="ri-calendar-line"></i>
            <span>${event.date} · ${event.time}</span>
          </div>
          <span class="inline-block px-3 py-1 rounded-full text-xs font-semibold ${
            event.isToday
              ? "bg-red-50 text-red-600 border border-red-200"
              : "bg-accent-100 text-accent-700 border border-accent-200"
          }">${event.isToday ? "Hoy" : "Próximamente"}</span>
          <p class="text-foreground-600 text-sm">${event.type}</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Crear recordatorio",
      cancelButtonText: "Cerrar",
      confirmButtonColor: "#0f172a",
      cancelButtonColor: "#e2e8f0",
      customClass: { popup: "rounded-2xl" },
    }).then((result) => {
      if (result.isConfirmed) {
        handleEventReminder(event.title);
      }
    });
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-sm border-b border-background-200 safe-top">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/app")}
            className="w-9 h-9 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 flex items-center justify-center cursor-pointer shrink-0 transition-all"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-accent-500/15 flex items-center justify-center">
              <i className="ri-fire-fill text-accent-500 text-lg"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-lg">Tendencias</h1>
              <p className="text-foreground-400 text-[11px]">
                Lo más escuchado · {timeFilter === "today" ? "Hoy" : timeFilter === "week" ? "Esta semana" : "Este mes"}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 md:px-6 pb-20">
        {/* Time Filter Pills */}
        <div className="sticky top-[57px] z-20 bg-background-50/95 backdrop-blur-sm pt-4 pb-3">
          <div className="inline-flex bg-background-100 rounded-full p-1 gap-0.5">
            {TIME_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setTimeFilter(f.key)}
                className={`px-5 py-2 rounded-full text-[13px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  timeFilter === f.key
                    ? "bg-primary-500 text-background-50 shadow-sm"
                    : "text-foreground-500 hover:text-foreground-700"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* ===== 1. Radios más escuchadas ===== */}
        <section className="mt-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary-500/15 flex items-center justify-center">
                <i className="ri-headphone-line text-primary-500 text-sm"></i>
              </div>
              <h2 className="text-foreground-950 font-bold text-sm">Radios más escuchadas</h2>
            </div>
            <span className="text-foreground-400 text-[11px] font-medium">
              {data.topListened.length} radios
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.topListened.map((radio) => (
              <TopListenedCard
                key={radio.id}
                radio={radio}
                onPlay={() => handlePlay(radio.name)}
                onFavorite={() => handleFavorite(radio.name)}
                onShare={() => handleShare(radio.name)}
                onNavigate={() => navigate(`/radio/${radio.frequency.replace(".", "-")}`)}
              />
            ))}
          </div>
        </section>

        {/* ===== 2. Radios en crecimiento ===== */}
        <section className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-accent-500/15 flex items-center justify-center">
                <i className="ri-funds-line text-accent-500 text-sm"></i>
              </div>
              <h2 className="text-foreground-950 font-bold text-sm">Radios en crecimiento</h2>
            </div>
            <span className="text-foreground-400 text-[11px] font-medium">
              {data.growing.length} radios
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.growing.map((radio) => (
              <GrowingCard
                key={radio.id}
                radio={radio}
                onPlay={() => handlePlay(radio.name)}
                onFavorite={() => handleFavorite(radio.name)}
                onNavigate={() => navigate(`/radio/${radio.frequency.replace(".", "-")}`)}
              />
            ))}
          </div>
        </section>

        {/* ===== 3. Lives populares ===== */}
        <section className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
                <i className="ri-live-line text-red-500 text-sm"></i>
              </div>
              <h2 className="text-foreground-950 font-bold text-sm">Lives populares</h2>
            </div>
            <span className="text-foreground-400 text-[11px] font-medium">
              {data.popularLives.length} en vivo
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.popularLives.map((live) => (
              <PopularLiveCard
                key={live.id}
                live={live}
                onPlay={() => handlePlay(live.name)}
                onNavigate={() => navigate(`/radio/${live.frequency.replace(".", "-")}`)}
              />
            ))}
          </div>
        </section>

        {/* ===== 4. Eventos destacados ===== */}
        <section className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-secondary-500/15 flex items-center justify-center">
                <i className="ri-calendar-event-line text-secondary-500 text-sm"></i>
              </div>
              <h2 className="text-foreground-950 font-bold text-sm">Eventos destacados</h2>
            </div>
            <span className="text-foreground-400 text-[11px] font-medium">
              {data.featuredEvents.length} eventos
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.featuredEvents.map((event) => (
              <FeaturedEventCard
                key={event.id}
                event={event}
                onClick={() => handleEventDetail(event)}
              />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

/* ─── Card Components ─── */

function TopListenedCard({
  radio,
  onPlay,
  onFavorite,
  onShare,
  onNavigate,
}: {
  radio: TrendingRadio;
  onPlay: () => void;
  onFavorite: () => void;
  onShare: () => void;
  onNavigate: () => void;
}) {
  return (
    <div className="group bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-4 transition-all duration-200 cursor-pointer" onClick={onNavigate}>
      <div className="flex items-center gap-3">
        {/* Rank */}
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-extrabold shrink-0 ${
            radio.rank === 1
              ? "bg-accent-500 text-background-50"
              : radio.rank === 2
                ? "bg-foreground-800 text-background-50"
                : radio.rank === 3
                  ? "bg-foreground-600 text-background-50"
                  : "bg-background-100 text-foreground-400"
          }`}
        >
          {radio.rank}
        </div>
        {/* Logo */}
        <div className="w-12 h-12 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
          <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
        {/* Info */}
        <div className="min-w-0 flex-1">
          <h3 className="text-foreground-950 font-semibold text-[13px] truncate">{radio.name}</h3>
          <p className="text-foreground-400 text-[11px] mt-0.5">
            {radio.frequency} · {radio.city}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-foreground-700 font-bold text-xs">
              {radio.listeners >= 1000 ? `${(radio.listeners / 1000).toFixed(1)}K` : radio.listeners}
            </span>
            <span className="text-foreground-400 text-[10px]">oyentes</span>
            <span
              className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${
                radio.rankChange === "up" ? "text-emerald-600" : radio.rankChange === "down" ? "text-red-500" : "text-foreground-400"
              }`}
            >
              <i
                className={`text-[10px] ${
                  radio.rankChange === "up"
                    ? "ri-arrow-up-s-line"
                    : radio.rankChange === "down"
                      ? "ri-arrow-down-s-line"
                      : "ri-subtract-line"
                }`}
              ></i>
              {radio.trendPercent}
            </span>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-100">
        <button
          onClick={(e) => { e.stopPropagation(); onPlay(); }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-primary-500 text-background-50 hover:bg-primary-600 text-[12px] font-semibold cursor-pointer transition-all whitespace-nowrap"
        >
          <i className="ri-play-fill text-sm"></i>
          Escuchar
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onFavorite(); }}
          className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-accent-500 transition-all"
        >
          <i className="ri-heart-line text-sm"></i>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onShare(); }}
          className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600 transition-all"
        >
          <i className="ri-share-forward-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

function GrowingCard({
  radio,
  onPlay,
  onFavorite,
  onNavigate,
}: {
  radio: GrowingRadio;
  onPlay: () => void;
  onFavorite: () => void;
  onNavigate: () => void;
}) {
  return (
    <div className="group bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-4 transition-all duration-200 cursor-pointer" onClick={onNavigate}>
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
          <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-foreground-950 font-semibold text-[13px] truncate">{radio.name}</h3>
          <p className="text-foreground-400 text-[11px]">{radio.frequency}</p>
        </div>
        {/* Growth badge */}
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-emerald-50 text-emerald-600 border border-emerald-200 whitespace-nowrap shrink-0">
          <i className="ri-arrow-up-line text-[10px]"></i>
          {radio.growthPercent}
        </span>
      </div>
      <div className="flex items-center gap-4 mt-2.5 text-[11px]">
        <div className="flex items-center gap-1">
          <i className="ri-user-add-line text-foreground-400"></i>
          <span className="text-foreground-600 font-semibold">+{radio.newListeners >= 1000 ? `${(radio.newListeners / 1000).toFixed(1)}K` : radio.newListeners} nuevos</span>
        </div>
        <div className="flex items-center gap-1">
          <i className="ri-headphone-line text-foreground-400"></i>
          <span className="text-foreground-600 font-semibold">
            {radio.listeners >= 1000 ? `${(radio.listeners / 1000).toFixed(1)}K` : radio.listeners} total
          </span>
        </div>
      </div>
      {/* Score bar */}
      <div className="mt-3 pt-3 border-t border-background-100">
        <div className="flex items-center gap-2">
          <span className="text-foreground-400 text-[10px] font-medium whitespace-nowrap">Score</span>
          <div className="flex-1 h-1.5 bg-background-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-accent-500 transition-all"
              style={{ width: `${(radio.radioScore / 10) * 100}%` }}
            ></div>
          </div>
          <span className="text-foreground-600 text-[10px] font-bold tabular-nums">{radio.radioScore.toFixed(1)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button
          onClick={(e) => { e.stopPropagation(); onPlay(); }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-primary-500 text-background-50 hover:bg-primary-600 text-[12px] font-semibold cursor-pointer transition-all whitespace-nowrap"
        >
          <i className="ri-play-fill text-sm"></i>
          Escuchar
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onFavorite(); }}
          className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-accent-500 transition-all"
        >
          <i className="ri-heart-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

function PopularLiveCard({
  live,
  onPlay,
  onNavigate,
}: {
  live: PopularLive;
  onPlay: () => void;
  onNavigate: () => void;
}) {
  return (
    <div className="group bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-4 transition-all duration-200 cursor-pointer relative overflow-hidden" onClick={onNavigate}>
      {/* Live pulse indicator top-right */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
        </span>
        <span className="text-[10px] font-extrabold text-red-500 uppercase tracking-wide">En vivo</span>
      </div>
      <div className="flex items-center gap-3 mt-1">
        <div className="w-14 h-14 rounded-xl overflow-hidden bg-background-100 border-2 border-red-200 shrink-0">
          <img src={live.logoUrl} alt={live.name} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-foreground-950 font-semibold text-[13px] truncate">{live.name}</h3>
          <p className="text-foreground-400 text-[11px] mt-0.5">{live.frequency}</p>
          <p className="text-foreground-500 text-[11px] mt-0.5 truncate">
            <i className="ri-broadcast-line text-[10px] mr-1"></i>
            {live.programName}
          </p>
          <p className="text-foreground-400 text-[10px] mt-0.5">
            {live.hostName} · {live.currentListeners >= 1000 ? `${(live.currentListeners / 1000).toFixed(1)}K` : live.currentListeners} escuchando
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-100">
        <button
          onClick={(e) => { e.stopPropagation(); onPlay(); }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600 text-[12px] font-semibold cursor-pointer transition-all whitespace-nowrap"
        >
          <i className="ri-play-fill text-sm"></i>
          Unirse
        </button>
        <span className="inline-flex items-center gap-1 text-foreground-400 text-[10px]">
          <i className="ri-user-line"></i>
          {live.currentListeners >= 1000 ? `${(live.currentListeners / 1000).toFixed(1)}K` : live.currentListeners}
        </span>
      </div>
    </div>
  );
}

function FeaturedEventCard({
  event,
  onClick,
}: {
  event: FeaturedEvent;
  onClick: () => void;
}) {
  return (
    <div className="group bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl overflow-hidden transition-all duration-200 cursor-pointer" onClick={onClick}>
      <div className="relative">
        <div className="w-full h-40 sm:h-48 overflow-hidden">
          <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        {/* Date badge */}
        <div className="absolute top-3 left-3">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold border ${
              event.isToday
                ? "bg-red-500 text-white border-red-400"
                : "bg-background-50/90 text-foreground-900 border-background-200"
            }`}
          >
            <i className={`text-[10px] ${event.isToday ? "ri-flashlight-line" : "ri-calendar-line"}`}></i>
            {event.isToday ? "HOY" : event.date}
          </span>
        </div>
        {/* Type badge */}
        <div className="absolute top-3 right-3">
          <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold bg-background-50/80 text-foreground-700 backdrop-blur-sm">
            {event.type}
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <img src={event.stationLogo} alt={event.stationName} className="w-6 h-6 rounded-md object-cover" loading="lazy" />
          <span className="text-foreground-500 text-[11px] font-medium">{event.stationName}</span>
        </div>
        <h3 className="text-foreground-950 font-semibold text-[13px] leading-snug">{event.title}</h3>
        <div className="flex items-center gap-2 mt-2 text-foreground-400 text-[11px]">
          <i className="ri-time-line"></i>
          <span>{event.time}</span>
          {event.isUpcoming && (
            <span className="inline-flex items-center gap-1 text-accent-600 font-semibold">
              <i className="ri-notification-3-line"></i>
              Recordarme
            </span>
          )}
        </div>
      </div>
    </div>
  );
}